<template>
  <asc-spinner-wrapper :loading="loading">
    <div class="asc-mxrec-followup--wrapper">
      <v-row class="form-wrapper">
        <v-col cols="6" class="pb-0" no-gutters>
          <asc-text-field-input
            v-model="getRemovedPartNumber"
            label="Removed Part #"
            :readonly="!editable"
            :no-spacing="false"
            :max-length="50"
            class="asc-mxrec-followup--removed-part-no"
          />
          <asc-text-field-input
            v-model="getRemovedPartSerialNumber"
            label="Removed Serial #"
            :readonly="!editable"
            :no-spacing="false"
            :max-length="50"
            class="asc-mxrec-followup--removed-serial-no"
          />
          <asc-text-field-input
            v-model="getPurchaseOrderNumber"
            label="Repair Purchase Order #"
            :readonly="!editable"
            :no-spacing="false"
            :max-length="50"
            class="asc-mxrec-followup--repair-purchase-order"
          />
        </v-col>
        <v-col cols="6" class="pb-0">
          <asc-date-picker
            v-model="getDateCompleted"
            label="Mx Completion Date"
            format="DD MMM YYYY"
            icon-position="right"
            :readonly="!editable"
            class="asc-mxrec-followup--completion-date"
          />
          <asc-autocomplete-selector
            v-if="!isCustomAlert && !isAirlineTier3OrTier4"
            v-model="getAirlineResponse"
            no-array
            label="Airline Response"
            :items="airlineResponseOptions"
            :sort-items="false"
            :readonly="!editable"
            display-clear
            class="asc-mxrec-followup--airline-response"
          />
          <asc-text-field-input
            v-model="getInstalledPartNumber"
            label="Installed Part #"
            :readonly="!editable"
            :no-spacing="false"
            :max-length="50"
            class="asc-mxrec-followup--installed-part-no"
          />
          <asc-text-field-input
            v-model="getInstalledPartSerialNumber"
            label="Installed Serial #"
            :readonly="!editable"
            :no-spacing="false"
            :max-length="50"
            class="asc-mxrec-followup--installed-serial-no"
          />
        </v-col>
        <v-col cols="6" class="pt-0 pb-0">
          <asc-text-field-input
            v-if="
              (isEditPermissionInvestigate ||
                isReadOnlyPermissionInvestigate) &&
              !isCustomAlert &&
              !isRecommendationTab
            "
            v-model="getServiceAlertNumber"
            label="Service Alert #"
            :readonly="!editable"
            :no-spacing="false"
            :max-length="50"
            class="asc-mxrec-followup--service-alert-no"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <asc-file-upload-input
            v-if="!isAirlineTier3OrTier4 && !isRecommendationTab"
            v-model="files"
            title="Attachments - 10 Maximum"
            :accepted-file-properties="fileTypeProperties"
            :allow-file-remove="!isReadOnlyPermissionInvestigate"
            :allow-file-upload="!isReadOnlyPermissionInvestigate"
            :on-upload-file="handleAttachmentsUploadFile"
            :on-remove-file="handleAttachmentsRemoval"
            :on-download-file="handleDownloadFile"
            :readonly="!editable"
            :upload-error="mxActionAttachmentUploadError"
            :can-edit-attachment-captions="
              (isEditPermissionInvestigate && !isRecommendationTab) ||
              isMxRecEditPermission ||
              isCustomAlert
            "
            :handle-update-attachment-caption="handleUpdateAttachmentCaption"
            viewable
            class="asc-mxrec-followup--attachments"
          ></asc-file-upload-input>
        </v-col>
      </v-row>

      <asc-card
        v-if="!isAirlineTier3OrTier4 && !isRecommendationTab"
        class="asc-mxrec-details-discussion--wrapper"
        no-spacing
        flat
      >
        <asc-accordion class="asc-mxrec-details-discussion--accordion" flat>
          <asc-accordion-item first>
            <template #header>MX DETAILS DISCUSSION</template>
            <p
              v-if="
                (isEditPermissionInvestigate && !isRecommendationTab) ||
                isMxRecEditPermission
              "
              class="intro"
            >
              Add a comment to the Collins and Airline communication thread. An
              email notification will be sent to tagged recipients upon saving.
            </p>
            <asc-user-comment-list-wrapper
              :user-comments="detailsDiscussion"
              :user="user"
              :discussion-type-id="detailsDiscussionTypeId"
              :status-updates="detailsStatusUpdates"
              :user-list="usersToTag"
              :reset-comment="resetComment"
              :restore-discussions="restoreDiscussions"
              :disabled="!editable || isDetailsDiscussionLoading"
              editor-placeholder="Add a comment. Click 'Save' to submit to Mx Rec."
              helper-text="Click 'Save' to post to Mx Rec."
              class="mt-4 mx-4"
              @addUserComment="handleAddUserComment"
              @deleteComment="handleDeleteUserComment"
              @updateComment="handleUpdateUserComment"
              @removeUnsavedComment="handleRemoveUnsavedComment"
            ></asc-user-comment-list-wrapper>
          </asc-accordion-item>
        </asc-accordion>
      </asc-card>
    </div>
  </asc-spinner-wrapper>
</template>

<script>
import AscAccordion from '@/components/atoms/AscAccordion'
import AscAccordionItem from '@/components/atoms/AscAccordionItem'
import AscCard from '@/components/atoms/AscCard'
import AscDatePicker from '@/components/atoms/AscDatePicker'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscTextFieldInput from '@/components/atoms/AscTextFieldInput'
import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscFileUploadInput from '@/components/molecules/AscFileUploadInput'
import AscUserCommentListWrapper from '@/components/organisms/AscUserCommentListWrapper'
import variables from '@/styles/_varsJs.scss'

import { COLLINS_PHM_TIER_3, COLLINS_PHM_TIER_4 } from '@/utilities/appConsts'

export default {
  name: 'AscMxrecFollowUpWrapper',

  components: {
    AscDatePicker,
    AscTextFieldInput,
    AscFileUploadInput,
    AscAutocompleteSelector,
    AscUserCommentListWrapper,
    AscSpinnerWrapper,
    AscCard,
    AscAccordion,
    AscAccordionItem,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    files: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Object,
      default: () => {},
    },
    fileTypeProperties: {
      type: Object,
      default: () => {},
    },
    isInternalView: {
      type: Boolean,
      default: false,
    },
    mxActionAttachmentUploadError: {
      type: Array,
      default: () => [],
    },
    airlineResponseOptions: {
      type: Array,
      default: () => [],
    },
    addUserComment: {
      type: Function,
      default: () => {},
    },
    deleteComment: {
      type: Function,
      default: () => {},
    },
    updateComment: {
      type: Function,
      default: () => {},
    },
    detailsDiscussion: {
      type: Array,
      default: () => [],
    },
    detailsStatusUpdates: {
      type: Array,
      default: () => [],
    },
    detailsDiscussionTypeId: {
      type: Number,
      default: null,
    },
    user: {
      type: Object,
      default: () => {},
    },
    usersToTag: {
      type: Array,
      default: () => {},
    },
    disabled: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    resetComment: {
      type: Boolean,
      default: false,
    },
    handleUpdateAttachmentCaption: {
      type: Function,
      default: () => {},
    },
    restoreDiscussions: {
      type: Boolean,
      default: false,
    },
    isDetailsDiscussionLoading: {
      type: Boolean,
      default: false,
    },
    isCustomAlert: {
      type: Boolean,
      default: false,
    },
    lockEnabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      maintenanceAttachments: [],
      subheaderColor: variables.gray1000,
    }
  },

  computed: {
    editable() {
      if (this.lockEnabled) return false
      if (this.isCustomAlert) return true
      if (this.isReadOnlyPermissionInvestigate && !this.isCustomAlert)
        return false
      else if (this.isMxRecEditPermission) return true
      return this.isEditPermissionInvestigate
    },
    getAirlineResponse: {
      get() {
        return this.data.airlineResponseId
      },
      set(val) {
        this.emitChangeEvent({
          airlineResponseId: val,
        })
      },
    },
    getInstalledPartNumber: {
      get() {
        return this.data.installedPartNumber
      },
      set(val) {
        this.emitChangeEvent({
          installedPartNumber: val,
        })
      },
    },
    getServiceAlertNumber: {
      get() {
        return this.data.serviceAlertNumber
      },
      set(val) {
        this.emitChangeEvent({
          serviceAlertNumber: val,
        })
      },
    },
    getPurchaseOrderNumber: {
      get() {
        return this.data.purchaseOrderNumber
      },
      set(val) {
        this.emitChangeEvent({
          purchaseOrderNumber: val,
        })
      },
    },
    getDateCompleted: {
      get() {
        return this.data.dateCompleted
      },
      set(val) {
        const date = val
          ? typeof val === 'string'
            ? val
            : typeof val === 'object'
            ? val?.dates?.startDate
            : null
          : null
        if (val) {
          this.emitChangeEvent({
            dateCompleted: date,
          })
        }
      },
    },
    getRemovedPartNumber: {
      get() {
        return this.data.removedPartNumber
      },
      set(val) {
        this.emitChangeEvent({
          removedPartNumber: val,
        })
      },
    },
    getRemovedPartSerialNumber: {
      get() {
        return this.data.removedPartSerialNumber
      },
      set(val) {
        this.emitChangeEvent({
          removedPartSerialNumber: val,
        })
      },
    },
    getInstalledPartSerialNumber: {
      get() {
        return this.data.installedPartSerialNumber
      },
      set(val) {
        this.emitChangeEvent({
          installedPartSerialNumber: val,
        })
      },
    },

    isAirlineResponseVisible() {
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 ? false : true
    },

    isAirlineTier3OrTier4() {
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 || COLLINS_PHM_TIER_4
        ? true
        : false
    },
  },

  watch: {
    files(newValue) {
      if (newValue.length) {
        this.selectedRows = 0
        // eslint-disable-next-line
        newValue.map(fileObj => {
          if (fileObj?.isExternallyVisible) {
            this.selectedRows++
          }
        })
        this.$nextTick(() => {
          this.maintenanceAttachments = newValue.filter(file =>
            file?.filename.match('MaintIssue'),
          )
        })
      }
    },
    mxActionAttachmentUploadError(newValue) {
      if (newValue) {
        this.maintenanceAttachments = this.maintenanceAttachments.filter(
          item => item.displayName !== newValue[0]?.name,
        )
      }
    },
  },

  methods: {
    handleAttachmentsUploadFile(file) {
      this.$emit('fileUploaded', {
        attachmentTypeId: this.fileTypeProperties.id,
        fileUpload: file,
        isExternallyVisible: true,
      })
    },
    handleAttachmentsRemoval(file) {
      this.maintenanceAttachments = this.maintenanceAttachments.filter(
        att => att.displayName !== file.displayName,
      )
      // pending files will not have an id, but will have a file name passed instead
      this.handleRemoveFile(file)
    },
    handleRemoveFile(file) {
      if (Number.isInteger(file?.id)) {
        this.$emit('fileSelected', {
          remove: true,
          id: file.id,
        })
      } else {
        this.$emit('fileSelected', {
          cancel: true,
          file,
        })
      }
    },
    handleDownloadFile(fileToDownload) {
      this.$emit('downloadFile', fileToDownload)
    },
    handleAddUserComment(commentInfo) {
      this.saveDisabled = false
      this.$emit('addUserComment', commentInfo)
    },
    handleDeleteUserComment(commentInfo) {
      this.saveDisabled = false
      this.$emit('deleteComment', commentInfo)
    },
    handleUpdateUserComment(commentInfo) {
      this.saveDisabled = false
      this.$emit('updateComment', commentInfo)
    },
    handleRemoveUnsavedComment(comment) {
      this.$emit('removeUnsavedComment', comment)
    },
    emitChangeEvent(propToOverwrite) {
      const mergedInfo = {
        ...this.data,
        ...propToOverwrite,
      }
      this.$emit('updateFollowUpFields', mergedInfo)
      this.$emit('followUpDataChanges', true)
    },
  },
}
</script>

<style lang="scss">
.asc-mxrec-followup {
  &--wrapper {
    padding: 16px 0;

    .form-wrapper {
      .asc-autocomplete-selector {
        margin-bottom: 24px;

        .asc-base-selector--label {
          height: 12px;
          margin-top: 4px;

          .asc-required-label {
            line-height: 12px;
          }
        }

        .asc-base-selector--input-container {
          height: calc(24px + 8px) !important;
          align-items: center;

          .asc-base-selector--input {
            height: 24px;
            padding: 8px 0 8px;
          }
        }
      }
    }

    .asc-mxrec-details-discussion--wrapper {
      margin-top: 24px;

      .asc-accordion--container {
        &.asc-mxrec-details-discussion--accordion {
          overflow-y: initial;

          .asc-accordion {
            .asc-accordion--item {
              border: 1px solid $gray-400;

              .asc-accordion--item--content {
                .intro {
                  margin-top: 5px;
                  margin-bottom: 28px;
                }
              }
            }
          }
        }
      }
    }

    .asc-spinner-wrapper {
      height: 100%;
    }
  }
}
</style>
